/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Language } from './types';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { HomeView } from './components/HomeView';
import { WhatsAppButton } from './components/WhatsAppButton';
import { MobileQuickBar } from './components/MobileQuickBar';
import { SERVICES } from './data/translations';
import { updatePageSeo } from './utils/seo';
import { fetchServerSiteConfig } from './data/siteConfig';
import { resolveRoute, getPathForView } from './utils/routes';
import { getCityBySlug } from './data/citiesData';
import { getRegionalZoneBySlug } from './data/regionalZonesData';

// Lazy loaded non-critical routes to shrink initial bundle
const ServicesView = lazy(() => import('./components/ServicesView').then(m => ({ default: m.ServicesView })));
const ZonesView = lazy(() => import('./components/ZonesView').then(m => ({ default: m.ZonesView })));
const AboutView = lazy(() => import('./components/AboutView').then(m => ({ default: m.AboutView })));
const ContactView = lazy(() => import('./components/ContactView').then(m => ({ default: m.ContactView })));
const AdminView = lazy(() => import('./components/AdminView').then(m => ({ default: m.AdminView })));
const ServiceDetailPage = lazy(() => import('./components/ServiceDetailPage').then(m => ({ default: m.ServiceDetailPage })));
const CityLandingPage = lazy(() => import('./components/CityLandingPage').then(m => ({ default: m.CityLandingPage })));
const RegionalZonePage = lazy(() => import('./components/RegionalZonePage').then(m => ({ default: m.RegionalZonePage })));
const SchemaInspectorModal = lazy(() => import('./components/SchemaInspectorModal').then(m => ({ default: m.SchemaInspectorModal })));

export default function App() {
  const initialRoute = resolveRoute(
    typeof window !== 'undefined' ? window.location.pathname : '/',
    typeof window !== 'undefined' ? window.location.search : ''
  );

  const [currentLang, setCurrentLang] = useState<Language>(initialRoute.lang);
  const [currentView, setCurrentView] = useState<string>(initialRoute.view);
  const [schemaModalOpen, setSchemaModalOpen] = useState(false);

  // Initialize clean URL on initial load if incoming link was legacy
  useEffect(() => {
    if (initialRoute.shouldReplaceUrl && typeof window !== 'undefined') {
      window.history.replaceState({ view: initialRoute.view, lang: initialRoute.lang }, '', initialRoute.targetPath);
    }
  }, []);

  // Listen to browser Back/Forward (popstate)
  useEffect(() => {
    const handlePopState = () => {
      const resolved = resolveRoute(window.location.pathname, window.location.search);
      setCurrentView(resolved.view);
      setCurrentLang(resolved.lang);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Centralized view updater with clean HTML5 pushState
  const handleSetView = (view: string) => {
    setCurrentView(view);
    const targetPath = getPathForView(view, currentLang === 'fr' ? undefined : currentLang);
    if (window.location.pathname + window.location.search !== targetPath) {
      window.history.pushState({ view, lang: currentLang }, '', targetPath);
    }
  };

  // Centralized language updater with URL sync
  const handleSetLang = (lang: Language) => {
    setCurrentLang(lang);
    const targetPath = getPathForView(currentView, lang === 'fr' ? undefined : lang);
    if (window.location.pathname + window.location.search !== targetPath) {
      window.history.pushState({ view: currentView, lang }, '', targetPath);
    }
  };

  useEffect(() => {
    // Charge la configuration et les images officielles stockées sur le serveur
    fetchServerSiteConfig();
  }, []);

  useEffect(() => {
    updatePageSeo(currentView, currentLang);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView, currentLang]);

  const activeServiceId = currentView.startsWith('service-') ? currentView.replace('service-', '') : null;
  const activeService = SERVICES.find(s => s.id === activeServiceId);

  const activeCitySlug = currentView.startsWith('zone-') ? currentView.replace('zone-', '') : null;
  const activeCity = activeCitySlug ? getCityBySlug(activeCitySlug) : null;
  const activeRegionalZone = activeCitySlug ? getRegionalZoneBySlug(activeCitySlug) : null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans selection:bg-orange-600 selection:text-white pb-16 lg:pb-0">
      <Header
        currentLang={currentLang}
        setLang={handleSetLang}
        currentView={currentView}
        setView={handleSetView}
        onOpenSchema={() => setSchemaModalOpen(true)}
      />

      <main className="flex-grow">
        <Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div></div>}>
          {currentView === 'home' && (
            <HomeView
              currentLang={currentLang}
              setView={handleSetView}
              onOpenSchema={() => setSchemaModalOpen(true)}
            />
          )}
          {currentView === 'services' && (
            <ServicesView
              currentLang={currentLang}
              setView={handleSetView}
            />
          )}
          {activeService && (
            <ServiceDetailPage
              service={activeService}
              currentLang={currentLang}
              onBack={() => handleSetView('services')}
              onNavigateContact={() => handleSetView('contact')}
              onNavigateView={handleSetView}
            />
          )}
          {currentView === 'zones' && (
            <ZonesView
              currentLang={currentLang}
              setView={handleSetView}
            />
          )}
          {activeCity && (
            <CityLandingPage
              city={activeCity}
              currentLang={currentLang}
              onBackToZones={() => handleSetView('zones')}
              onNavigateContact={() => handleSetView('contact')}
              onNavigateService={(srv) => handleSetView(srv)}
              onNavigateView={handleSetView}
            />
          )}
          {activeRegionalZone && (
            <RegionalZonePage
              zone={activeRegionalZone}
              currentLang={currentLang}
              onBackToZones={() => handleSetView('zones')}
              onNavigateContact={() => handleSetView('contact')}
              onNavigateCity={(slug) => handleSetView(`zone-${slug}`)}
              onNavigateService={(srv) => handleSetView(srv)}
              onNavigateView={handleSetView}
            />
          )}
          {currentView === 'about' && (
            <AboutView
              currentLang={currentLang}
              setView={handleSetView}
            />
          )}
          {currentView === 'contact' && (
            <ContactView
              currentLang={currentLang}
              setView={handleSetView}
            />
          )}
          {currentView === 'admin' && (
            <AdminView
              currentLang={currentLang}
              setView={handleSetView}
            />
          )}
        </Suspense>
      </main>

      <Footer
        currentLang={currentLang}
        setView={handleSetView}
        onOpenSchema={() => setSchemaModalOpen(true)}
      />

      <WhatsAppButton currentLang={currentLang} />
      <MobileQuickBar currentLang={currentLang} onNavigateContact={() => handleSetView('contact')} />

      {schemaModalOpen && (
        <Suspense fallback={null}>
          <SchemaInspectorModal
            currentLang={currentLang}
            onClose={() => setSchemaModalOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
}
